import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { BreadcrumbJsonLd, JsonLd, useOgTags } from "@/components/store/SeoJsonLd";
import ProductCard from "@/components/store/ProductCard";

/** Markdown-lite renderer: ## headings, **bold**, > quotes, - lists, paragraphs. */
function Body({ text }) {
  const blocks = (text || "").split(/\n\n+/);
  return (
    <div className="space-y-4 text-stone-700 leading-relaxed text-[15px]" data-testid="guide-body">
      {blocks.map((b, i) => {
        if (b.startsWith("## ")) {
          const [head, ...rest] = b.split("\n");
          return (
            <div key={i}>
              <h2 className="font-heading text-xl font-semibold pt-3">{head.slice(3)}</h2>
              {rest.length > 0 && <p className="mt-2">{inline(rest.join(" "))}</p>}
            </div>
          );
        }
        if (b.startsWith("> ")) return <p key={i} className="border-l-2 border-gold pl-4 text-stone-500 italic">{b.slice(2)}</p>;
        if (b.startsWith("- ") || b.startsWith("1. ")) {
          return (
            <ul key={i} className="list-disc ml-5 space-y-1">
              {b.split("\n").map((li, j) => <li key={j}>{inline(li.replace(/^[-\d.]+\s*/, "").replace(/^\d+\.\s*/, ""))}</li>)}
            </ul>
          );
        }
        return <p key={i}>{inline(b)}</p>;
      })}
    </div>
  );
}

function inline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p);
}

export default function GuideDetail() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setGuide(null); setNotFound(false);
    api.get(`/guides/${slug}`)
      .then((r) => {
        setGuide(r.data);
        document.title = r.data.meta_title || `${r.data.title} | Siyaara`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
        meta.content = r.data.meta_description || "";
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
        link.href = `https://siyaara.com/guides/${slug}`;
        const cat = (r.data.related_categories || [])[0];
        if (cat) api.get("/products", { params: { category: cat, limit: 4 } }).then((pr) => setRelated(pr.data.items || [])).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  useOgTags(guide ? {
    title: guide.meta_title || guide.title, description: guide.meta_description,
    image: guide.hero_image, url: `https://siyaara.com/guides/${slug}`, type: "article",
  } : {});

  if (notFound) return <div className="container-x py-20 text-center" data-testid="guide-404"><h1 className="font-heading text-2xl font-semibold">Guide not found</h1><Link to="/guides" className="text-brand underline mt-3 inline-block">All guides</Link></div>;
  if (!guide) return <div className="container-x py-20 text-center text-stone-400 font-mono text-sm">LOADING…</div>;

  return (
    <div className="container-x py-10 max-w-3xl" data-testid="guide-page">
      <BreadcrumbJsonLd items={[{ name: "Home", url: "/" }, { name: "Guides", url: "/guides" }, { name: guide.title, url: `/guides/${slug}` }]} />
      <JsonLd id="article-jsonld" data={{
        "@context": "https://schema.org", "@type": "Article",
        headline: guide.title, description: guide.meta_description,
        author: { "@type": "Organization", name: guide.author },
        publisher: { "@id": "https://siyaara.com/#org" },
        ...(guide.published_at && { datePublished: guide.published_at, dateModified: guide.updated_at || guide.published_at }),
        ...(guide.hero_image && { image: guide.hero_image }),
        mainEntityOfPage: `https://siyaara.com/guides/${slug}`,
      }} />
      <nav className="text-[11px] font-mono text-stone-400 uppercase tracking-widest">
        <Link to="/" className="hover:text-brand">Home</Link> / <Link to="/guides" className="hover:text-brand">Guides</Link>
      </nav>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2" data-testid="guide-title">{guide.title}</h1>
      <p className="text-xs font-mono text-stone-400 mt-2">{guide.author}{guide.published_at ? ` · ${new Date(guide.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}` : ""}</p>
      {guide.hero_image && <img src={guide.hero_image} alt={guide.title} className="w-full aspect-[2/1] object-cover rounded-lg mt-6" />}
      <div className="mt-8"><Body text={guide.body} /></div>

      <div className="mt-10 border border-brand/20 bg-brand-light/40 rounded-lg p-5" data-testid="guide-cta">
        <p className="font-heading font-semibold">See it priced live</p>
        <p className="text-sm text-stone-600 mt-1">Every Siyaara piece shows its full price breakup off today's rate — the exact math these guides explain.</p>
        <Link to="/shop" className="inline-block mt-3 px-5 py-2.5 bg-brand text-ivory text-sm rounded-full">Browse the catalogue</Link>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <p className="eyebrow">RELATED PIECES</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
